import { Link, useLocation } from "react-router-dom";
import { useMemo } from "react";
import { Building2, Landmark, LogOut, Settings, User, Users } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { useClinicsForUser, useSwitchClinic } from "@/hooks/useClinics";
import { useCurrentClinicId } from "@/hooks/useAppointments";
import { useHasPermissionSync } from "@/hooks/usePermissions";

interface AccountMenuItemsProps {
  onSelect?: () => void;
}

export function AccountMenuItems({ onSelect }: AccountMenuItemsProps) {
  const { signOut } = useAuth();
  const location = useLocation();
  const { t } = useTranslation("nav");
  const { data: clinics = [] } = useClinicsForUser();
  const { data: currentClinicId } = useCurrentClinicId();
  const switchClinic = useSwitchClinic();
  const { hasPermission: canViewTeam } = useHasPermissionSync("team", "view");

  const clinicsByCustomer = useMemo(() => {
    return clinics.reduce<Record<string, typeof clinics>>((groups, clinic) => {
      groups[clinic.customer_id] = [...(groups[clinic.customer_id] ?? []), clinic];
      return groups;
    }, {});
  }, [clinics]);

  const customerIds = Object.keys(clinicsByCustomer);

  const handleClinicChange = (clinicId: string) => {
    if (clinicId === currentClinicId || switchClinic.isPending) return;

    switchClinic.mutate(clinicId, {
      onSuccess: () => onSelect?.(),
    });
  };

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  return (
    <div className="space-y-1">
      <Link
        to="/profile"
        onClick={onSelect}
        aria-current={isActive("/profile") ? "page" : undefined}
        className={`
          flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
          ${isActive("/profile")
            ? "bg-primary/10 text-primary"
            : "text-foreground hover:bg-accent"
          }
        `}
      >
        <User className="h-5 w-5" aria-hidden="true" />
        <span className="text-sm font-medium">{t("profile")}</span>
      </Link>
      <Link
        to="/settings"
        onClick={onSelect}
        aria-current={isActive("/settings") ? "page" : undefined}
        className={`
          flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
          ${isActive("/settings")
            ? "bg-primary/10 text-primary"
            : "text-foreground hover:bg-accent"
          }
        `}
      >
        <Settings className="h-5 w-5" aria-hidden="true" />
        <span className="text-sm font-medium">{t("settings")}</span>
      </Link>
      {canViewTeam && (
        <Link
          to="/team"
          onClick={onSelect}
          aria-current={isActive("/team") ? "page" : undefined}
          className={`
            flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
            ${isActive("/team") ? "bg-primary/10 text-primary" : "text-foreground hover:bg-accent"}
          `}
        >
          <Users className="h-5 w-5" aria-hidden="true" />
          <span className="text-sm font-medium">{t("team")}</span>
        </Link>
      )}
      <Link
        to="/taxes/settings"
        onClick={onSelect}
        aria-current={isActive("/taxes") ? "page" : undefined}
        className={`
          flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
          ${isActive("/taxes")
            ? "bg-primary/10 text-primary"
            : "text-foreground hover:bg-accent"
          }
        `}
      >
        <Landmark className="h-5 w-5" aria-hidden="true" />
        <span className="text-sm font-medium">{t("taxes")}</span>
      </Link>
      {clinics.length > 1 ? (
        <div className="pt-2 border-t">
          <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t("accountMenu.selectClinic")}
          </div>
          {customerIds.map((customerId) => {
            const customerClinics = clinicsByCustomer[customerId];
            const showCustomerName = customerIds.length > 1;

            return (
              <div key={customerId}>
                {showCustomerName ? (
                  <div className="px-4 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {customerClinics[0]?.customer_name}
                  </div>
                ) : null}
                {customerClinics.map((clinic) => {
                  const isActiveClinic = clinic.id === currentClinicId;

                  return (
                    <button
                      key={clinic.id}
                      type="button"
                      disabled={isActiveClinic || switchClinic.isPending}
                      aria-current={isActiveClinic ? "true" : undefined}
                      onClick={() => handleClinicChange(clinic.id)}
                      className={`
                        flex items-center gap-3 w-full px-4 py-3 rounded-lg transition-colors disabled:opacity-100
                        ${isActiveClinic ? "bg-primary/10 text-primary" : "text-foreground hover:bg-accent"}
                      `}
                    >
                      <Building2 className="h-5 w-5 shrink-0" aria-hidden="true" />
                      <span className="text-sm font-medium truncate flex-1 text-left">{clinic.name}</span>
                      {isActiveClinic ? (
                        <span className="sr-only">{t("accountMenu.activeClinic")}</span>
                      ) : null}
                      {switchClinic.isPending && !isActiveClinic ? (
                        <span className="text-xs text-muted-foreground">{t("accountMenu.switchingClinic")}</span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      ) : null}
      <div className="pt-2 border-t">
        <button
          type="button"
          onClick={() => {
            signOut();
            onSelect?.();
          }}
          className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-destructive hover:bg-destructive/10"
        >
          <LogOut className="h-5 w-5" aria-hidden="true" />
          <span className="text-sm font-medium">{t("signOut")}</span>
        </button>
      </div>
    </div>
  );
}
